import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouter,
  type ParsedLocation,
  type RouterHistory,
} from '@tanstack/react-router';
import { IMPORT_KINDS } from '@wodi/shared';
import { RouterProvider as AriaRouterProvider, ToastRegion } from '@wodi/ui';
import { z } from 'zod';
import { PrescribePage } from './routes/prescribe-page.tsx';
import { FormularySettingsPage } from './routes/settings-formulary.tsx';
import { DoseRulesPage } from './routes/settings-dose-rules.tsx';
import { PharmacyItemPage } from './routes/settings-pharmacy-item.tsx';
import { PharmacyItemsSettingsPage } from './routes/settings-pharmacy-items.tsx';

import { sessionQuery } from './api/session.ts';
import { AppLayout } from './routes/app-layout.tsx';
import { BillingApprovalsPage } from './routes/billing-approvals.tsx';
import { PaybillPaymentsPage } from './routes/cashier-paybill.tsx';
import { CashierShiftPage } from './routes/cashier-shift.tsx';
import { CashierPage } from './routes/cashier.tsx';
import { BillingSettingsPage } from './routes/settings-billing.tsx';
import { EtimsSettingsPage } from './routes/settings-etims.tsx';
import { ShaSettingsPage } from './routes/settings-sha.tsx';
import { PractitionersPage } from './routes/settings-practitioners.tsx';
import { AppointmentsPage } from './routes/appointments.tsx';
import { AppointmentSettingsPage } from './routes/settings-appointments.tsx';
import { ClaimPage, ClaimsPage, PreauthPage } from './routes/claims.tsx';
import {
  InsuranceApprovalsPage,
  InsuranceClaimsPage,
  InsuranceDebtorsPage,
  InsuranceInvoicePage,
  InsuranceInvoicesPage,
  InsurancePaymentPage,
  InsurancePaymentsPage,
  RecordPaymentPage,
} from './routes/insurance.tsx';
import { InsuranceClaimPage } from './routes/insurance-claim.tsx';
import { ErrorPage } from './routes/error-page.tsx';
import { HomePage } from './routes/home.tsx';
import { NotFoundPage } from './routes/not-found.tsx';
import { PatientPage } from './routes/patient-page.tsx';
import { PharmacyCounterPage } from './routes/pharmacy-counter.tsx';
import { PharmacyDispensingPage } from './routes/pharmacy-dispensing.tsx';
import { PharmacyReturnsPage } from './routes/pharmacy-returns.tsx';
import { ReceptionPage } from './routes/reception.tsx';
import { QueueBoardPage } from './routes/queue-board.tsx';
import { ConsultationWorkspacePage } from './consultation/workspace-page.tsx';
import { QueueDisplayPage } from './routes/queue-display.tsx';
import { ClinicalRulesPage } from './routes/settings-clinical-rules.tsx';
import { QueueDisplaysPage } from './routes/settings-queue-displays.tsx';
import { RegisterPatientPage } from './routes/register-patient.tsx';
import { RegisterNewPatientButton } from './patients/register-new-patient-button.tsx';
import { can } from './api/session.ts';
import { SignInCodePage } from './routes/sign-in-code.tsx';
import { SignInSetupPage } from './routes/sign-in-setup.tsx';
import { SignInPage } from './routes/sign-in.tsx';
import { NumberFormatsPage } from './routes/settings-numbers.tsx';
import { PayersPage } from './routes/settings-payers.tsx';
import { ServiceItemPage } from './routes/settings-service-item.tsx';
import { ServicePointsPage } from './routes/settings-service-points.tsx';
import { ServicesPage } from './routes/settings-services.tsx';
import { UsersPage } from './routes/settings-users.tsx';
import { UserPage } from './routes/settings-user.tsx';
import { AccountLinkPage } from './routes/account-link.tsx';
import { RoleUpdatesPage } from './settings/role-updates-page.tsx';
import { OrganisationSettingsPage } from './routes/settings-organisation.tsx';
import { AccountPage } from './routes/account.tsx';
import { RegistrationSettingsPage } from './routes/settings-registration.tsx';
import { SettingsOverviewPage } from './routes/settings-overview.tsx';
import { ImportDetailPage } from './routes/settings-import-detail.tsx';
import { ImportsPage } from './routes/settings-imports.tsx';
import { TerminologyPage } from './routes/settings-terminology.tsx';
import { StoresSettingsPage } from './routes/settings-stores.tsx';
import { ReceiveGoodsPage } from './routes/receive-goods.tsx';
import { RequestAdjustmentPage } from './routes/request-adjustment.tsx';
import { StockCountPage } from './routes/stock-count.tsx';
import { StockItemPage } from './routes/stock-item.tsx';
import { StoresPage } from './routes/stores.tsx';
import { GoodsReceivedNotePage, GoodsReceivedPage } from './routes/goods-received.tsx';
import {
  PurchaseOrdersPage,
  PurchaseRequisitionsPage,
  SupplierInvoicesPage,
  SuppliersPage,
} from './routes/procurement.tsx';
import { NewPurchaseOrderPage, NewPurchaseRequisitionPage } from './routes/procurement-new.tsx';
import { PurchaseOrderPage } from './routes/purchase-order.tsx';
import { PurchaseOrderPrintPage } from './routes/purchase-order-print.tsx';
import { PurchaseRequisitionPage } from './routes/purchase-requisition.tsx';
import { StoreRequisitionPage } from './routes/store-requisition.tsx';
import { NewStoreRequisitionPage, StoreRequisitionsPage } from './routes/store-requisitions.tsx';
import { SupplierInvoicePage } from './routes/supplier-invoice.tsx';
import { NewSupplierInvoicePage } from './routes/supplier-invoice-new.tsx';
import { SupplierStatementPage } from './routes/supplier-statement.tsx';
import { TransferStockPage } from './routes/transfer-stock.tsx';
import { LabSamplePage } from './routes/lab-sample.tsx';
import { LabTestEditorPage, LabTestsPage } from './routes/settings-lab.tsx';
import { LabAnalytePage, LabAnalytesPage } from './routes/settings-lab-analytes.tsx';
import { LabFacilitySettingsPage, LabListsPage } from './routes/settings-lab-lists.tsx';
import { LabWorklistPage } from './routes/lab-worklist.tsx';
import { LabAnalyzerResultsPage } from './routes/lab-analyzer-results.tsx';
import { LabAnalyzerPage, LabAnalyzersPage } from './routes/settings-lab-analyzers.tsx';
import { LabQcPage, LabQcSeriesPage } from './lab/qc-pages.tsx';
import { LabMicroListsPage } from './routes/settings-lab-micro.tsx';
import { LabQcSettingsPage } from './routes/settings-lab-qc.tsx';
import { IMAGING_WORKLIST_STATES, LAB_WORKLIST_STATES } from '@wodi/shared';
import { CollectionsReportPage } from './routes/report-collections.tsx';
import { DaySummaryPage } from './routes/report-day-summary.tsx';
import { PharmacyRegisterPage } from './routes/report-pharmacy-register.tsx';
import { LabRegisterPage, OpdRegisterPage } from './routes/report-registers.tsx';
import { RevenueReportPage } from './routes/report-revenue.tsx';
import { UnclaimedReportPage } from './routes/report-unclaimed.tsx';
import { MohFormPage, ReportsPage } from './routes/reports.tsx';
import { KhisSettingsPage } from './khis/settings-page.tsx';
import { KhisSubmissionPage } from './khis/submission-page.tsx';
import { KhisSubmissionsPage } from './khis/submissions-page.tsx';
import { QualityPage } from './quality/quality-page.tsx';
import { AnalyticsPage } from './analytics/analytics-page.tsx';
import { toasts } from './toasts.ts';
import { AdmissionPage } from './inpatient/admission-page.tsx';
import { AdmitPage } from './inpatient/admit-page.tsx';
import { BedBoardPage } from './inpatient/bed-board-page.tsx';
import { PackagesSettingsPage } from './inpatient-billing/settings-packages-page.tsx';
import { WardsSettingsPage } from './inpatient/settings-wards-page.tsx';
import { CasualtyPage } from './casualty/casualty-page.tsx';
import { IdentifyPage } from './casualty/identify-page.tsx';
import { PatientMergePage, PatientMergesPage } from './casualty/merges-page.tsx';
import { NursingPage } from './inpatient/nursing-page.tsx';
import { MedicinesPage } from './inpatient/medicines-page.tsx';
import { PharmacyWardOrdersPage } from './routes/pharmacy-ward-orders.tsx';
import { MedicinesSettingsPage } from './routes/settings-medicines.tsx';
import { WardRoundPage } from './inpatient/ward-round-page.tsx';
import { WardWorklistPage } from './inpatient/ward-worklist-page.tsx';
import { AccessLogPage } from './records/access-log-page.tsx';
import { NewRecordsRequestPage } from './records/new-request-page.tsx';
import { ClosedRequestsPage, OpenRequestsPage, ReleaseLogPage } from './records/records-page.tsx';
import { RecordsRequestPage } from './records/request-page.tsx';
import { ChartsOutPage, OverdueChartsPage } from './records/charts-page.tsx';
import { PatientChartPage } from './records/patient-chart-page.tsx';
import { RecordsSettingsPage } from './records/settings-records-page.tsx';
import { AuditLogPage } from './privacy/audit-log-page.tsx';
import { BreakGlassPage } from './privacy/break-glass-page.tsx';
import { LabelsSettingsPage } from './privacy/labels-settings-page.tsx';
import { RetentionSettingsPage } from './privacy/retention-settings-page.tsx';
import {
  AccountingAccountsPage,
  AccountingJournalPage,
  AccountingPeriodPage,
  AccountingPeriodsPage,
} from './routes/accounting.tsx';
import {
  MyEarningsPage,
  MyStatementPage,
  RevenueShareRulesPage,
  RevenueShareSettingsPage,
  RevenueShareStatementPage,
  RevenueShareStatementsPage,
} from './routes/revenue-share.tsx';
import { StaffRecordPage } from './staff/record-page.tsx';
import { IncidentPage } from './incidents/incident-page.tsx';
import { IncidentsPage } from './incidents/incidents-page.tsx';
import { ReportIncidentPage } from './incidents/report-page.tsx';
import { OperationsListsSettingsPage } from './operations/lists-settings-page.tsx';
import { InfectionControlPage } from './infection/infection-page.tsx';
import { KitchenPage } from './kitchen/kitchen-page.tsx';
import { MealTicketsPage } from './kitchen/meal-tickets-page.tsx';
import { WardDietsPage } from './kitchen/ward-diets-page.tsx';
import { HousekeepingPage } from './housekeeping/housekeeping-page.tsx';
import { HousekeepingSettingsPage } from './housekeeping/settings-housekeeping-page.tsx';
import { AssetPage } from './assets/asset-page.tsx';
import { AssetsPage } from './assets/assets-page.tsx';
import { WorkOrderPage } from './assets/work-order-page.tsx';
import { AttendanceReportPage, MyAttendancePage } from './roster/attendance-pages.tsx';
import { RosterPage } from './roster/roster-page.tsx';
import { LicencesToRenewPage, StaffPage } from './staff/staff-page.tsx';
import { ImagingExamPage } from './imaging/exam-page.tsx';
import { ReferralPage } from './referrals/referral-page.tsx';
import { AncPage } from './maternity/anc-page.tsx';
import { ChildHealthPage } from './maternity/child-health-page.tsx';
import { LabourPage } from './maternity/labour-page.tsx';
import { LabourWardPage } from './maternity/labour-ward-page.tsx';
import { MaternityPage } from './maternity/maternity-page.tsx';
import { MaternitySettingsPage } from './maternity/settings-maternity-page.tsx';
import { ReferralsPage } from './referrals/referrals-page.tsx';
import { ImagingSettingsPage } from './imaging/settings-imaging-page.tsx';
import { ImagingWorklistPage } from './imaging/worklist-page.tsx';
import { BookCasePage } from './theatre/book-case-page.tsx';
import { TheatreCasePage } from './theatre/case-page.tsx';
import { TheatresSettingsPage } from './theatre/settings-theatres-page.tsx';
import { MortuaryCasePage } from './mortuary/case-page.tsx';
import { MortuaryPage } from './mortuary/mortuary-page.tsx';
import { ReceiveBodyPage } from './mortuary/receive-page.tsx';
import { MortuarySettingsPage } from './mortuary/settings-mortuary-page.tsx';
import { BirthPage, RecordBirthPage } from './vital-events/birth-pages.tsx';
import { DeathPage } from './vital-events/death-page.tsx';
import { RecordDeathPage } from './vital-events/record-death-page.tsx';
import { VitalEventsPage } from './vital-events/registers-page.tsx';
import { TheatreListPage } from './theatre/theatre-list-page.tsx';
import { FeedbackLinkPage } from './portal/feedback-link-page.tsx';
import { PortalHomePage } from './portal/home-page.tsx';
import { PortalSignInPage } from './portal/sign-in-page.tsx';
import { PortalSettingsPage } from './portal/settings-portal-page.tsx';
import { FeedbackInboxPage } from './feedback/inbox-page.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

/** Lets React Aria links and menus navigate through TanStack Router instead of reloading. */
function Root() {
  const router = useRouter();
  return (
    <AriaRouterProvider
      navigate={(to) => {
        void router.navigate({ to });
      }}
      useHref={(to) => router.buildLocation({ to }).href}
    >
      <Outlet />
      <ToastRegion queue={toasts} />
    </AriaRouterProvider>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFoundPage,
});

const nextSearch = z.object({ next: z.string().max(500).optional() });

/**
 * The waiting-area display (triage plan §6.2; ADR 0013): outside the app shell and any
 * staff session. It authenticates with its own device cookie and shows tickets only.
 */
const queueDisplayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/display',
  component: QueueDisplayPage,
});

/**
 * The patient portal and feedback links (docs/plans/phase3-portal-feedback-kiswahili.md §7.1):
 * outside the staff shell and any staff session, with their own sign-in.
 */
export const portalSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$code',
  validateSearch: z.object({ reason: z.enum(['expired', 'signed_out']).optional() }),
  component: PortalSignInPage,
});

export const portalHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$code/home',
  component: PortalHomePage,
});

export const feedbackLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/f/$token',
  component: FeedbackLinkPage,
});

/**
 * A one-time setup or password reset link (ADR 0037): outside the shell and any session.
 * The token is in the fragment, never the path or query.
 */
export const accountLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-link',
  component: AccountLinkPage,
});

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: nextSearch.extend({
    reason: z.enum(['expired', 'expired_unsent', 'signed_out']).optional(),
  }),
  component: SignInPage,
});

/** The 2FA steps need a session that has passed the password but not the second factor. */
async function requirePendingSession({ context }: { context: RouterContext }) {
  const session = await context.queryClient.query(sessionQuery);
  if (session === null) throw redirect({ to: '/sign-in' });
  if (!session.twoFactor.pending) throw redirect({ to: '/' });
  return { session };
}

export const signInCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/code',
  validateSearch: nextSearch,
  beforeLoad: requirePendingSession,
  component: SignInCodePage,
});

export const signInSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/set-up-2fa',
  validateSearch: nextSearch,
  beforeLoad: requirePendingSession,
  component: SignInSetupPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: async ({
    context,
    location,
  }: {
    context: RouterContext;
    location: ParsedLocation;
  }) => {
    const session = await context.queryClient.query(sessionQuery);
    if (session === null) throw redirect({ to: '/sign-in', search: { next: location.href } });
    if (session.twoFactor.pending) {
      throw redirect({
        to: session.twoFactor.enrolled ? '/sign-in/code' : '/sign-in/set-up-2fa',
        search: { next: location.href },
      });
    }
    return { session };
  },
  component: AppLayout,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });

// Each person's own settings (ADR 0030): open to anyone signed in.
const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsOverviewPage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/users',
  component: UsersPage,
});

const userRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/users/$userId',
  component: UserPage,
});

const roleUpdatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/role-updates',
  component: RoleUpdatesPage,
});

const organisationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/organisation',
  component: OrganisationSettingsPage,
});

const registrationSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/registration',
  component: RegistrationSettingsPage,
});

const servicePointsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/service-points',
  component: ServicePointsPage,
});

const numberFormatsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/numbers',
  component: NumberFormatsPage,
});

const terminologyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/terminology',
  component: TerminologyPage,
});

const importsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/imports',
  // "Import prices" and "Import opening balances" open New import with these chosen.
  validateSearch: z.object({
    kind: z.enum(IMPORT_KINDS).optional(),
    priceListId: z.uuid().optional(),
    storeId: z.uuid().optional(),
  }),
  component: ImportsRoute,
});

function ImportsRoute() {
  const preset = importsRoute.useSearch();
  return <ImportsPage preset={preset} />;
}

const importDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/imports/$importId',
  params: {
    parse: (raw: { importId: string }) => ({ importId: z.uuid().parse(raw.importId) }),
    stringify: ({ importId }: { importId: string }) => ({ importId }),
  },
  component: ImportDetailRoute,
});

function ImportDetailRoute() {
  const { importId } = importDetailRoute.useParams();
  return <ImportDetailPage importId={importId} />;
}
const servicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/services',
  component: ServicesPage,
});

const serviceItemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/services/$itemId',
  params: {
    parse: (raw: { itemId: string }) => ({ itemId: z.uuid().parse(raw.itemId) }),
    stringify: ({ itemId }: { itemId: string }) => ({ itemId }),
  },
  component: function ServiceItemRoute() {
    const { itemId } = serviceItemRoute.useParams();
    return <ServiceItemPage itemId={itemId} />;
  },
});

const payersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/payers',
  component: PayersPage,
});

/** Internal UUIDs only; never a patient number (CLAUDE.md, domain rule 8). */
const patientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId',
  params: {
    parse: (raw: { patientId: string }) => ({ patientId: z.uuid().parse(raw.patientId) }),
    stringify: ({ patientId }: { patientId: string }) => ({ patientId }),
  },
  component: PatientPage,
});

const receptionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reception',
  // The chosen patient's internal ID only. Search text never goes in the address.
  // Checking in for an appointment adds its internal ID (appointments plan §6).
  validateSearch: z.object({ patientId: z.uuid().optional(), appointmentId: z.uuid().optional() }),
  component: ReceptionRoute,
});

function ReceptionRoute() {
  const { session } = appRoute.useRouteContext();
  return (
    <ReceptionPage
      {...(can(session, 'patients.register')
        ? { registerAction: (text: string) => <RegisterNewPatientButton searchText={text} /> }
        : {})}
    />
  );
}

// Queues and triage (docs/plans/phase1-triage.md §6.1). Internal UUIDs only.
const queueBoardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queues',
  validateSearch: z.object({
    servicePointId: z.uuid().optional(),
    entryId: z.uuid().optional(),
  }),
  component: QueueBoardPage,
});

// The consultation workspace (docs/plans/phase1-consultation.md §7.1). The visit's internal ID only.
const consultationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/consultation',
  params: {
    parse: (raw: { visitId: string }) => ({ visitId: z.uuid().parse(raw.visitId) }),
    stringify: ({ visitId }: { visitId: string }) => ({ visitId }),
  },
  component: function ConsultationRoute() {
    const { visitId } = consultationRoute.useParams();
    return <ConsultationWorkspacePage key={visitId} visitId={visitId} />;
  },
});

const clinicalRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/clinical-rules',
  component: ClinicalRulesPage,
});

const queueDisplaysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/queue-displays',
  component: QueueDisplaysPage,
});

// Stores (docs/plans/phase1-stock.md §5). Addresses carry internal UUIDs only.
const uuidParams = <K extends string>(...keys: K[]) => ({
  parse: (raw: Record<K, string>) =>
    Object.fromEntries(keys.map((k) => [k, z.uuid().parse(raw[k])])) as Record<K, string>,
  stringify: (params: Record<K, string>) => params,
});

const storesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores',
  validateSearch: z.object({ storeId: z.uuid().optional() }),
  component: StoresPage,
});

const stockItemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/items/$itemId',
  params: uuidParams('storeId', 'itemId'),
  component: StockItemPage,
});

const receiveGoodsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/receive',
  params: uuidParams('storeId'),
  // Receiving against an LPO (docs/plans/phase2-procurement.md §5.7).
  validateSearch: z.object({ orderId: z.uuid().optional() }),
  component: ReceiveGoodsPage,
});

const transferStockRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/transfer',
  params: uuidParams('storeId'),
  component: TransferStockPage,
});

const requestAdjustmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/adjust',
  params: uuidParams('storeId'),
  component: RequestAdjustmentPage,
});

const stockCountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/counts/$countId',
  params: uuidParams('storeId', 'countId'),
  component: StockCountPage,
});

// Procurement (docs/plans/phase2-procurement.md §5). Addresses carry internal UUIDs only.
const goodsReceivedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/receipts',
  params: uuidParams('storeId'),
  component: GoodsReceivedPage,
});

const goodsReceivedNoteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/receipts/$grnId',
  params: uuidParams('storeId', 'grnId'),
  component: GoodsReceivedNotePage,
});

const storeRequisitionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/requisitions',
  params: uuidParams('storeId'),
  component: StoreRequisitionsPage,
});

const newStoreRequisitionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/requisitions/new',
  params: uuidParams('storeId'),
  component: NewStoreRequisitionPage,
});

const storeRequisitionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stores/$storeId/requisitions/$requisitionId',
  params: uuidParams('storeId', 'requisitionId'),
  component: StoreRequisitionPage,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement',
  component: PurchaseOrdersPage,
});

const newPurchaseOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/orders/new',
  validateSearch: z.object({ requisitionId: z.uuid().optional() }),
  component: NewPurchaseOrderPage,
});

const purchaseOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/orders/$orderId',
  params: uuidParams('orderId'),
  component: PurchaseOrderPage,
});

const purchaseOrderPrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/orders/$orderId/print',
  params: uuidParams('orderId'),
  component: PurchaseOrderPrintPage,
});

const purchaseRequisitionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/requisitions',
  component: PurchaseRequisitionsPage,
});

const newPurchaseRequisitionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/requisitions/new',
  component: NewPurchaseRequisitionPage,
});

const purchaseRequisitionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/requisitions/$requisitionId',
  params: uuidParams('requisitionId'),
  component: PurchaseRequisitionPage,
});

const supplierInvoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/invoices',
  component: SupplierInvoicesPage,
});

const newSupplierInvoiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/invoices/new',
  validateSearch: z.object({ orderId: z.uuid() }),
  component: NewSupplierInvoicePage,
});

const supplierInvoiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/invoices/$invoiceId',
  params: uuidParams('invoiceId'),
  component: SupplierInvoicePage,
});

const suppliersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/suppliers',
  component: SuppliersPage,
});

const supplierStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/suppliers/$supplierId/statement',
  params: uuidParams('supplierId'),
  component: SupplierStatementPage,
});

const storesSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/stores',
  component: StoresSettingsPage,
});

// Billing and the cashier (docs/plans/phase1-billing.md §6). The visit's internal ID only.
const cashierRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cashier',
  validateSearch: z.object({ visitId: z.uuid().optional() }),
  component: CashierPage,
});

const cashierShiftRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cashier/shift',
  component: CashierShiftPage,
});

// Paybill (M-Pesa C2B) payments waiting to be matched (ADR 0020).
const cashierPaybillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cashier/paybill',
  component: PaybillPaymentsPage,
});

const billingApprovalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/billing/approvals',
  component: BillingApprovalsPage,
});

const billingSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/billing',
  component: BillingSettingsPage,
});

// eTIMS signing modes (docs/plans/phase3-etims-vscu.md §6; ADR 0041).
const etimsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/etims',
  component: EtimsSettingsPage,
});

// The pharmacy (docs/plans/phase1-dispensing.md §8). No patient data in addresses.
const pharmacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pharmacy',
  validateSearch: z.object({ prescriptionId: z.uuid().optional() }),
  component: PharmacyDispensingPage,
});

const pharmacyCounterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pharmacy/counter',
  component: PharmacyCounterPage,
});

const pharmacyReturnsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pharmacy/returns',
  component: PharmacyReturnsPage,
});

// Inpatient pharmacy (docs/plans/phase2-emar-inpatient-pharmacy.md §5).
const pharmacyWardOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pharmacy/ward-orders',
  component: PharmacyWardOrdersPage,
});

// The lab (docs/plans/phase1-lab.md §8). The tab and internal IDs only; a scanned sample
// number is looked up in a request body, never put in the address.
const labRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab',
  validateSearch: z.object({ state: z.enum(LAB_WORKLIST_STATES).optional() }),
  component: LabWorklistPage,
});

const labSampleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/samples/$sampleId',
  params: uuidParams('sampleId'),
  component: LabSamplePage,
});

// Analyzer results to review (docs/plans/phase2-lab-connector.md §6). No sample numbers or
// values in the address.
const labAnalyzerResultsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/analyzer-results',
  component: LabAnalyzerResultsPage,
});

// Lab quality control (docs/plans/phase3-lab-qc-and-microbiology.md §5). Internal IDs only.
const labQcRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/qc',
  component: LabQcPage,
});

const labQcSeriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/lab/qc/$targetId',
  params: uuidParams('targetId'),
  component: LabQcSeriesPage,
});

// Radiology RIS lite (docs/plans/phase2-radiology.md §6). The list and internal IDs only.
const imagingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/imaging',
  validateSearch: z.object({ state: z.enum(IMAGING_WORKLIST_STATES).optional() }),
  component: ImagingWorklistPage,
});

const imagingExamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/imaging/exams/$orderId',
  params: uuidParams('orderId'),
  component: ImagingExamPage,
});

const imagingSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/imaging',
  component: ImagingSettingsPage,
});

// Settings > Lab (lab plan §8.1).
const labSettingsRoutes = [
  createRoute({ getParentRoute: () => appRoute, path: '/settings/lab', component: LabTestsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/tests/$serviceItemId',
    params: uuidParams('serviceItemId'),
    component: LabTestEditorPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/analytes',
    component: LabAnalytesPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/analytes/$analyteId',
    params: uuidParams('analyteId'),
    component: LabAnalytePage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/lists',
    component: LabListsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/settings',
    component: LabFacilitySettingsPage,
  }),
  // Lab analyzers (docs/plans/phase2-lab-connector.md §6).
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/analyzers',
    component: LabAnalyzersPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/analyzers/$analyzerId',
    params: uuidParams('analyzerId'),
    component: LabAnalyzerPage,
  }),
  // QC set-up and microbiology lists (docs/plans/phase3-lab-qc-and-microbiology.md §5).
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/qc',
    component: LabQcSettingsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/settings/lab/micro',
    component: LabMicroListsPage,
  }),
] as const;
// Reports (docs/plans/phase1-reports.md §9). Filters live in component state, never the
// address; a report linked from the day summary may carry only the day it opens on.
const reportDaySearch = z.object({
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
});

const daySummaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/day-summary',
  validateSearch: reportDaySearch,
  component: function DaySummaryRoute() {
    const { day } = daySummaryRoute.useSearch();
    return <DaySummaryPage key={day ?? ''} day={day} />;
  },
});

const collectionsReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/daily-collections',
  validateSearch: reportDaySearch,
  component: function CollectionsRoute() {
    const { day } = collectionsReportRoute.useSearch();
    return <CollectionsReportPage key={day ?? ''} day={day} />;
  },
});

const revenueReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/revenue',
  validateSearch: reportDaySearch,
  component: function RevenueRoute() {
    const { day } = revenueReportRoute.useSearch();
    return <RevenueReportPage key={day ?? ''} day={day} />;
  },
});

const unclaimedReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/payer-share-unclaimed',
  component: UnclaimedReportPage,
});

const opdRegisterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/opd-register',
  validateSearch: reportDaySearch,
  component: function OpdRegisterRoute() {
    const { day } = opdRegisterRoute.useSearch();
    return <OpdRegisterPage key={day ?? ''} day={day} />;
  },
});

const labRegisterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/lab-register',
  validateSearch: reportDaySearch,
  component: function LabRegisterRoute() {
    const { day } = labRegisterRoute.useSearch();
    return <LabRegisterPage key={day ?? ''} day={day} />;
  },
});

const pharmacyRegisterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/pharmacy-register',
  validateSearch: reportDaySearch,
  component: function PharmacyRegisterRoute() {
    const { day } = pharmacyRegisterRoute.useSearch();
    return <PharmacyRegisterPage key={day ?? ''} day={day} />;
  },
});

const mohFormRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/moh/$form',
  component: function MohRoute() {
    const { form } = mohFormRoute.useParams();
    return <MohFormPage form={form} />;
  },
});

// KHIS submissions and quality indicators (docs/plans/phase3-khis-and-quality.md §4).
// Addresses carry internal IDs only.
const khisSubmissionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/khis',
  component: KhisSubmissionsPage,
});

const khisSubmissionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/khis/$submissionId',
  component: function KhisSubmissionRoute() {
    const { submissionId } = khisSubmissionRoute.useParams();
    return <KhisSubmissionPage key={submissionId} submissionId={submissionId} />;
  },
});

const qualityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/quality',
  component: QualityPage,
});

// Deeper analytics (docs/plans/phase3-deeper-analytics.md §5). The address carries only the
// area; filters stay in the page.
const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/analytics/$area',
  component: function AnalyticsRoute() {
    const { area } = analyticsRoute.useParams();
    return <AnalyticsPage key={area} area={area} />;
  },
});

const khisSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/khis',
  component: KhisSettingsPage,
});

// SHA (docs/plans/phase1-sha.md §9).
const shaSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/sha',
  component: ShaSettingsPage,
});

// Appointments (docs/plans/phase2-appointments.md §6). A day and an internal ID only.
const appointmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/appointments',
  validateSearch: z.object({
    day: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    scheduleId: z.uuid().optional(),
  }),
  component: AppointmentsPage,
});

const appointmentSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/appointments',
  component: AppointmentSettingsPage,
});

// Settings > Practitioners (docs/plans/phase1-consultation.md §7.4).
const practitionersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/practitioners',
  component: PractitionersPage,
});

const claimsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/claims',
  validateSearch: z.object({
    view: z
      .enum(['needs_attention', 'in_progress', 'settled', 'all'])
      .catch('needs_attention')
      .default('needs_attention'),
  }),
  component: ClaimsPage,
});

const preauthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/claims/preauth',
  component: PreauthPage,
});

/** The claim's internal ID only; never its number (domain rule 8). */
const claimRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/claims/$claimId',
  params: uuidParams('claimId'),
  component: ClaimPage,
});

// Private insurance (docs/plans/phase2-private-insurance.md §6). Internal IDs only in URLs.
const insuranceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance',
  validateSearch: z.object({
    view: z
      .enum(['to_send', 'with_insurer', 'rejected', 'settled', 'all'])
      .catch('to_send')
      .default('to_send'),
  }),
  component: InsuranceClaimsPage,
});
const insuranceRoutes = [
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/claims/$claimId',
    params: uuidParams('claimId'),
    component: InsuranceClaimPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/invoices',
    component: InsuranceInvoicesPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/invoices/$invoiceId',
    params: uuidParams('invoiceId'),
    component: InsuranceInvoicePage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/payments',
    component: InsurancePaymentsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/payments/new',
    component: RecordPaymentPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/payments/$remittanceId',
    params: uuidParams('remittanceId'),
    component: InsurancePaymentPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/debtors',
    component: InsuranceDebtorsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/insurance/approvals',
    component: InsuranceApprovalsPage,
  }),
];

const registerPatientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/new',
  component: RegisterPatientPage,
});

// Prescribing and the formulary (docs/plans/phase1-consultation.md §7.3, §7.5).
const prescribeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/prescribe',
  params: {
    parse: (raw: { visitId: string }) => ({ visitId: z.uuid().parse(raw.visitId) }),
    stringify: ({ visitId }: { visitId: string }) => ({ visitId }),
  },
  component: PrescribePage,
});

const formularySettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/formulary',
  component: FormularySettingsPage,
});

const doseRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/dose-rules',
  component: DoseRulesPage,
});

// Settings > Pharmacy items (dispensing plan §5.3): the list, and one item by its internal ID.
const pharmacyItemsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/pharmacy-items',
  component: PharmacyItemsSettingsPage,
});

const pharmacyItemSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/pharmacy-items/$itemId',
  params: {
    parse: (raw: { itemId: string }) => ({ itemId: z.uuid().parse(raw.itemId) }),
    stringify: ({ itemId }: { itemId: string }) => ({ itemId }),
  },
  component: function PharmacyItemRoute() {
    const { itemId } = pharmacyItemSettingsRoute.useParams();
    return <PharmacyItemPage itemId={itemId} />;
  },
});

// Inpatient ADT and the bed board (docs/plans/phase2-inpatient-adt.md §6). Internal IDs only.
const wardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wards',
  component: BedBoardPage,
});

const admissionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admissions/$admissionId',
  params: uuidParams('admissionId'),
  component: AdmissionPage,
});

// Ward rounds and nursing (docs/plans/phase2-ward-rounds-nursing.md §5). Internal IDs only.
const wardWorklistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wards/$wardId',
  params: uuidParams('wardId'),
  component: WardWorklistPage,
});

const wardRoundRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admissions/$admissionId/ward-round',
  params: uuidParams('admissionId'),
  component: WardRoundPage,
});

const nursingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admissions/$admissionId/nursing',
  params: uuidParams('admissionId'),
  component: NursingPage,
});

// The eMAR (docs/plans/phase2-emar-inpatient-pharmacy.md §5). Internal IDs only.
const medicinesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admissions/$admissionId/medicines',
  params: uuidParams('admissionId'),
  component: MedicinesPage,
});

const admitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/admit',
  params: uuidParams('visitId'),
  component: AdmitPage,
});

const wardsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/wards',
  component: WardsSettingsPage,
});

// Maternity and MCH (docs/plans/phase2-maternity-mch.md §6). Internal IDs only.
const labourWardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/maternity',
  component: LabourWardPage,
});

const patientMaternityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/maternity',
  params: uuidParams('patientId'),
  component: MaternityPage,
});

const ancRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/anc',
  params: uuidParams('visitId'),
  component: AncPage,
});

const labourRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/labour/$labourRecordId',
  params: uuidParams('labourRecordId'),
  component: LabourPage,
});

const childHealthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/child-health',
  params: uuidParams('patientId'),
  component: ChildHealthPage,
});

const maternitySettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/maternity',
  component: MaternitySettingsPage,
});

const medicinesSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/medicines',
  component: MedicinesSettingsPage,
});

// Casualty and record merges (docs/plans/phase2-casualty.md §5). Internal IDs only.
const casualtyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/casualty',
  component: CasualtyPage,
});

const identifyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/identify',
  params: uuidParams('patientId'),
  component: IdentifyPage,
});

const patientMergesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patient-merges',
  component: PatientMergesPage,
});

const patientMergeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patient-merges/$requestId',
  params: uuidParams('requestId'),
  component: PatientMergePage,
});

const packagesSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/packages',
  component: PackagesSettingsPage,
});

// Referrals (docs/plans/phase2-referrals.md §6). Internal IDs and the list's direction only.
const referralsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/referrals',
  validateSearch: z.object({ direction: z.enum(['outgoing', 'incoming']).optional() }),
  component: ReferralsPage,
});

const referralRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/referrals/$referralId',
  params: uuidParams('referralId'),
  component: ReferralPage,
});

// Staff records with licence expiry (docs/plans/phase2-staff-records.md §6). Internal IDs only.
const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff',
  component: StaffPage,
});

const licencesToRenewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff/licences',
  component: LicencesToRenewPage,
});

const staffRecordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff/$userId',
  params: uuidParams('userId'),
  component: StaffRecordPage,
});

// Accounting and doctor revenue share (docs/plans/phase3-accounting-and-revenue-share.md §6).
// Addresses carry internal IDs only.
const accountingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounting',
  component: AccountingJournalPage,
});

const accountingPeriodsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounting/periods',
  component: AccountingPeriodsPage,
});

const accountingPeriodRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounting/periods/$periodId',
  params: uuidParams('periodId'),
  component: AccountingPeriodPage,
});

const accountingAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounting/accounts',
  component: AccountingAccountsPage,
});

const revenueShareRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/revenue-share',
  component: RevenueShareStatementsPage,
});

const revenueShareStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/revenue-share/statements/$statementId',
  params: uuidParams('statementId'),
  component: RevenueShareStatementPage,
});

const revenueShareRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/revenue-share/rules',
  component: RevenueShareRulesPage,
});

const revenueShareSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/revenue-share/settings',
  component: RevenueShareSettingsPage,
});

const myEarningsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/my-earnings',
  component: MyEarningsPage,
});

const myStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/my-earnings/$statementId',
  params: uuidParams('statementId'),
  component: MyStatementPage,
});

// Incident reporting (docs/plans/phase3-operations-modules.md §1.4). Internal IDs only.
const incidentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/incidents',
  component: IncidentsPage,
});

const reportIncidentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/incidents/new',
  component: ReportIncidentPage,
});

const incidentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/incidents/$incidentId',
  params: uuidParams('incidentId'),
  component: IncidentPage,
});

// Infection control (docs/plans/phase3-operations-modules.md §2.4).
const infectionControlRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/infection-control',
  component: InfectionControlPage,
});

// Assets and maintenance (docs/plans/phase3-operations-modules.md §3.3). Internal IDs only.
const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets',
  component: AssetsPage,
});

const assetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets/$assetId',
  params: uuidParams('assetId'),
  component: AssetPage,
});

const workOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/work-orders/$workOrderId',
  params: uuidParams('workOrderId'),
  component: WorkOrderPage,
});

// Roster and attendance (docs/plans/phase3-operations-modules.md §4.3).
const rosterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roster',
  component: RosterPage,
});

const attendanceReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roster/attendance',
  component: AttendanceReportPage,
});

const myAttendanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/attendance',
  component: MyAttendancePage,
});

// Kitchen and housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §1.4, §2.4).
// Internal IDs, a day and a meal only.
const kitchenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kitchen',
  component: KitchenPage,
});

const mealTicketsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kitchen/tickets',
  validateSearch: z.object({
    wardId: z.uuid().optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    meal: z.enum(['breakfast', 'lunch', 'supper']),
  }),
  component: MealTicketsPage,
});

const wardDietsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/wards/$wardId/diets',
  params: uuidParams('wardId'),
  component: WardDietsPage,
});

const housekeepingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/housekeeping',
  component: HousekeepingPage,
});

const housekeepingSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/housekeeping',
  component: HousekeepingSettingsPage,
});

// Settings lists for the operations modules (ADR 0036).
const operationsListsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/operations-lists',
  component: OperationsListsSettingsPage,
});

// Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §6).
// Internal IDs only.
const recordsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/records',
  component: OpenRequestsPage,
});

const closedRecordsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/records/closed',
  component: ClosedRequestsPage,
});

const releaseLogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/records/releases',
  component: ReleaseLogPage,
});

const recordsRequestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/records/requests/$requestId',
  params: uuidParams('requestId'),
  component: RecordsRequestPage,
});

const newRecordsRequestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/records-requests/new',
  params: uuidParams('patientId'),
  component: NewRecordsRequestPage,
});

// Chart tracking (docs/plans/phase2-medical-records-charts.md §6). Internal IDs only.
const chartsOutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/charts',
  component: ChartsOutPage,
});

const overdueChartsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/charts/overdue',
  component: OverdueChartsPage,
});

const patientChartRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/chart',
  params: uuidParams('patientId'),
  component: PatientChartPage,
});

// Privacy tooling (docs/plans/golive-privacy-tooling.md). No IDs in these addresses.
const labelsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/sensitivity-labels',
  component: LabelsSettingsPage,
});

const auditLogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/audit-log',
  component: AuditLogPage,
});

const breakGlassRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/break-glass',
  component: BreakGlassPage,
});

const retentionSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/retention',
  component: RetentionSettingsPage,
});

const recordsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/records',
  component: RecordsSettingsPage,
});

const accessLogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/patients/$patientId/access-log',
  params: uuidParams('patientId'),
  component: AccessLogPage,
});

// Theatre lite (docs/plans/phase2-theatre.md §6). Internal IDs and a day only.
const theatreListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/theatre',
  validateSearch: z.object({
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .catch(undefined),
  }),
  component: TheatreListPage,
});

const theatreCaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/theatre/cases/$caseId',
  params: uuidParams('caseId'),
  component: TheatreCasePage,
});

const theatreBookingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/theatre-booking',
  params: uuidParams('visitId'),
  component: BookCasePage,
});

// Death and birth notification and the mortuary
// (docs/plans/phase2-mortuary-and-notifications.md §6). Internal IDs only.
const recordDeathRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/record-death',
  params: uuidParams('visitId'),
  component: RecordDeathPage,
});

const deathRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/deaths/$deathId',
  params: uuidParams('deathId'),
  component: DeathPage,
});

const recordBirthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/visits/$visitId/record-birth',
  params: uuidParams('visitId'),
  component: RecordBirthPage,
});

const birthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/births/$birthId',
  params: uuidParams('birthId'),
  component: BirthPage,
});

const vitalEventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/vital-events',
  component: VitalEventsPage,
});

const mortuaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mortuary',
  component: MortuaryPage,
});

const receiveBodyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mortuary/receive',
  validateSearch: z.object({ deathId: z.uuid().optional().catch(undefined) }),
  component: ReceiveBodyPage,
});

const mortuaryCaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mortuary/cases/$caseId',
  params: uuidParams('caseId'),
  component: MortuaryCasePage,
});

const mortuarySettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/mortuary',
  component: MortuarySettingsPage,
});

const theatresSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/theatres',
  component: TheatresSettingsPage,
});

const feedbackInboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/feedback',
  component: FeedbackInboxPage,
});

const portalSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/patient-portal',
  component: PortalSettingsPage,
});

const routeTree = rootRoute.addChildren([
  portalSignInRoute,
  portalHomeRoute,
  feedbackLinkRoute,
  accountLinkRoute,
  signInRoute,
  queueDisplayRoute,
  signInCodeRoute,
  signInSetupRoute,
  appRoute.addChildren([
    homeRoute,
    accountRoute,
    feedbackInboxRoute,
    portalSettingsRoute,
    settingsRoute,
    usersRoute,
    userRoute,
    organisationRoute,
    registrationSettingsRoute,
    servicePointsRoute,
    numberFormatsRoute,
    terminologyRoute,
    importsRoute,
    importDetailRoute,
    servicesRoute,
    serviceItemRoute,
    payersRoute,
    registerPatientRoute,
    patientRoute,
    receptionRoute,
    queueBoardRoute,
    consultationRoute,
    clinicalRulesRoute,
    queueDisplaysRoute,
    storesRoute,
    stockItemRoute,
    receiveGoodsRoute,
    transferStockRoute,
    requestAdjustmentRoute,
    stockCountRoute,
    storesSettingsRoute,
    goodsReceivedRoute,
    goodsReceivedNoteRoute,
    storeRequisitionsRoute,
    newStoreRequisitionRoute,
    storeRequisitionRoute,
    purchaseOrdersRoute,
    newPurchaseOrderRoute,
    purchaseOrderRoute,
    purchaseOrderPrintRoute,
    purchaseRequisitionsRoute,
    newPurchaseRequisitionRoute,
    purchaseRequisitionRoute,
    supplierInvoicesRoute,
    newSupplierInvoiceRoute,
    supplierInvoiceRoute,
    suppliersRoute,
    supplierStatementRoute,
    cashierRoute,
    cashierShiftRoute,
    cashierPaybillRoute,
    billingSettingsRoute,
    etimsSettingsRoute,
    pharmacyRoute,
    pharmacyCounterRoute,
    pharmacyReturnsRoute,
    pharmacyWardOrdersRoute,
    billingApprovalsRoute,
    labRoute,
    labSampleRoute,
    labAnalyzerResultsRoute,
    labQcRoute,
    labQcSeriesRoute,
    ...labSettingsRoutes,
    imagingRoute,
    imagingExamRoute,
    imagingSettingsRoute,
    reportsRoute,
    khisSubmissionsRoute,
    khisSubmissionRoute,
    qualityRoute,
    analyticsRoute,
    khisSettingsRoute,
    daySummaryRoute,
    collectionsReportRoute,
    revenueReportRoute,
    unclaimedReportRoute,
    opdRegisterRoute,
    labRegisterRoute,
    pharmacyRegisterRoute,
    mohFormRoute,
    prescribeRoute,
    formularySettingsRoute,
    doseRulesRoute,
    pharmacyItemsSettingsRoute,
    pharmacyItemSettingsRoute,
    shaSettingsRoute,
    practitionersRoute,
    appointmentsRoute,
    appointmentSettingsRoute,
    claimsRoute,
    preauthRoute,
    claimRoute,
    insuranceRoute,
    ...insuranceRoutes,
    wardsRoute,
    wardWorklistRoute,
    admissionRoute,
    wardRoundRoute,
    nursingRoute,
    medicinesRoute,
    admitRoute,
    wardsSettingsRoute,
    medicinesSettingsRoute,
    casualtyRoute,
    identifyRoute,
    patientMergesRoute,
    patientMergeRoute,
    packagesSettingsRoute,
    recordsRoute,
    staffRoute,
    licencesToRenewRoute,
    staffRecordRoute,
    accountingRoute,
    accountingPeriodsRoute,
    accountingPeriodRoute,
    accountingAccountsRoute,
    revenueShareRoute,
    revenueShareStatementRoute,
    revenueShareRulesRoute,
    revenueShareSettingsRoute,
    myEarningsRoute,
    myStatementRoute,
    incidentsRoute,
    reportIncidentRoute,
    incidentRoute,
    operationsListsSettingsRoute,
    infectionControlRoute,
    kitchenRoute,
    mealTicketsRoute,
    wardDietsRoute,
    housekeepingRoute,
    housekeepingSettingsRoute,
    assetsRoute,
    assetRoute,
    workOrderRoute,
    rosterRoute,
    attendanceReportRoute,
    myAttendanceRoute,
    referralsRoute,
    referralRoute,
    labourWardRoute,
    patientMaternityRoute,
    ancRoute,
    labourRoute,
    childHealthRoute,
    maternitySettingsRoute,
    closedRecordsRoute,
    releaseLogRoute,
    recordsRequestRoute,
    newRecordsRequestRoute,
    accessLogRoute,
    chartsOutRoute,
    overdueChartsRoute,
    patientChartRoute,
    recordsSettingsRoute,
    labelsSettingsRoute,
    auditLogRoute,
    breakGlassRoute,
    retentionSettingsRoute,
    roleUpdatesRoute,
    theatreListRoute,
    theatreCaseRoute,
    theatreBookingRoute,
    theatresSettingsRoute,
    recordDeathRoute,
    deathRoute,
    recordBirthRoute,
    birthRoute,
    vitalEventsRoute,
    mortuaryRoute,
    receiveBodyRoute,
    mortuaryCaseRoute,
    mortuarySettingsRoute,
  ]),
  // The component gallery exists only in development builds; in production this
  // branch is `false` and the bundler drops it along with the lazy import.
  ...(import.meta.env.DEV
    ? [
        createRoute({
          getParentRoute: () => rootRoute,
          path: '/_ui',
          component: lazyRouteComponent(() => import('./routes/dev-gallery.tsx'), 'GalleryPage'),
        }),
        createRoute({
          getParentRoute: () => rootRoute,
          path: '/_print',
          component: lazyRouteComponent(() => import('./routes/dev-print.tsx'), 'PrintGalleryPage'),
        }),
      ]
    : []),
]);

/** `history` is for tests, which render the real route tree at a given address. */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    ...(history === undefined ? {} : { history }),
    context: { queryClient },
    defaultPreload: false,
    defaultErrorComponent: ErrorPage,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
